/**
 * OfferContextDisplay Component
 *
 * Displays offer context_json in a human-readable format based on assignment strategy.
 * Supports MIGRATION, MANUAL, and AUTO_DEFAULT strategies.
 * Also displays availability override fields when present.
 */

import React from 'react';
import { DataList } from '@/components/common/DataList';
import type {
  OfferContext,
  AssignmentStrategy,
  MigrationContext,
  ManualContext,
  AutoDefaultContext,
} from '@/app/types/offers';
import './OfferContextDisplay.scss';

interface OfferContextDisplayProps {
  context: OfferContext | null;
  strategy: AssignmentStrategy;
}

// Type guards for context types
function isMigrationContext(ctx: OfferContext): ctx is MigrationContext {
  return 'migration_source' in ctx;
}

function isManualContext(ctx: OfferContext): ctx is ManualContext {
  return 'assigned_by' in ctx;
}

function isAutoDefaultContext(ctx: OfferContext): ctx is AutoDefaultContext {
  return 'source' in ctx && (ctx as AutoDefaultContext).source === 'auto_default';
}

// Check if context has override fields
function hasOverrideFields(ctx: OfferContext): boolean {
  return 'available_override' in ctx;
}

export const OfferContextDisplay: React.FC<OfferContextDisplayProps> = ({ context, strategy }) => {
  if (!context) {
    return (
      <div className="offer-context-display empty">
        <p className="no-context">No context data available</p>
      </div>
    );
  }

  // Build items array based on strategy type
  const getMigrationItems = (ctx: MigrationContext) => [
    { label: 'Migration Source', value: ctx.migration_source },
    { label: 'Original Offer ID', value: ctx.original_offer_id, copyable: true },
    { label: 'Migrated At', value: new Date(ctx.migrated_at).toLocaleString() },
  ];

  const getManualItems = (
    ctx: ManualContext
  ): Array<{ label: string; value: string; copyable?: boolean }> => {
    const items: Array<{ label: string; value: string; copyable?: boolean }> = [
      { label: 'Assigned By', value: ctx.assigned_by },
      { label: 'Reason', value: ctx.reason },
    ];
    if (ctx.ticket_id) {
      items.push({ label: 'Ticket ID', value: ctx.ticket_id, copyable: true });
    }
    return items;
  };

  const getAutoDefaultItems = (
    ctx: AutoDefaultContext
  ): Array<{ label: string; value: string }> => [
    { label: 'Source', value: ctx.source },
    { label: 'Reward Key', value: ctx.reward_key },
    { label: 'Reward Variant', value: ctx.reward_variant },
    { label: 'Assigned At', value: new Date(ctx.assigned_at).toLocaleString() },
  ];

  // Get override items if present (can appear in any strategy)
  const getOverrideItems = (
    ctx: OfferContext
  ): Array<{ label: string; value: React.ReactNode }> => {
    const items: Array<{ label: string; value: React.ReactNode }> = [];
    const ctxRecord = ctx as Record<string, unknown>;

    if ('available_override' in ctxRecord) {
      const isGranted = ctxRecord.available_override === true;
      items.push({
        label: 'Availability Override',
        value: (
          <span className={`override-badge ${isGranted ? 'granted' : 'revoked'}`}>
            {isGranted ? '✓ Granted (Available)' : '✗ Revoked (Unavailable)'}
          </span>
        ),
      });
    }

    if ('override_reason' in ctxRecord && ctxRecord.override_reason) {
      items.push({
        label: 'Override Reason',
        value: ctxRecord.override_reason as string,
      });
    }

    if ('show_message_on_card' in ctxRecord) {
      items.push({
        label: 'Show on Card',
        value: ctxRecord.show_message_on_card ? 'Yes' : 'No',
      });
    }

    return items;
  };

  const renderGenericContext = (ctx: Record<string, unknown>) => (
    <div className="generic-context">
      <pre>{JSON.stringify(ctx, null, 2)}</pre>
    </div>
  );

  let items: Array<{ label: string; value: React.ReactNode; copyable?: boolean }> = [];

  if (isMigrationContext(context)) {
    items = getMigrationItems(context);
  } else if (isManualContext(context)) {
    items = getManualItems(context);
  } else if (isAutoDefaultContext(context)) {
    items = getAutoDefaultItems(context);
  }

  // Add override items if present
  if (hasOverrideFields(context)) {
    items = [...items, ...getOverrideItems(context)];
  }

  return (
    <div className="offer-context-display">
      <h4 className="context-header">Context Details ({strategy})</h4>
      {items.length > 0 ? (
        <DataList items={items} />
      ) : (
        renderGenericContext(context as Record<string, unknown>)
      )}
    </div>
  );
};

export default OfferContextDisplay;
